import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, MapPin, X } from "lucide-react";
import AddressAutocomplete from "components/AddressAutocomplete";

export type AddressFormValues = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  label?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
};

type AddressFormErrors = Partial<Record<keyof AddressFormValues, string>>;

export interface AddAddressModalProps {
  open: boolean;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  notice?: string;
  initialValues?: Partial<AddressFormValues>;
  onClose: () => void;
  onSubmit: (values: AddressFormValues) => void | Promise<void>;
}

const defaultValues: AddressFormValues = {
  firstName: "",
  lastName: "",
  phone: "",
  label: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "United States",
  cityCode: "",
  stateCode: "",
  countryCode: "US",
  latitude: 0,
  longitude: 0,
};

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

function buildInitialValues(initialValues?: Partial<AddressFormValues>): AddressFormValues {
  const merged = { ...defaultValues, ...initialValues };
  if (!merged.country?.trim()) {
    merged.country = US_COUNTRY_DEFAULTS.country;
    merged.countryCode = US_COUNTRY_DEFAULTS.countryCode;
  }
  return merged;
}

const inputBaseStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #dbe2ea",
  outline: "none",
  fontFamily: "Roboto, sans-serif",
  fontSize: 15,
  lineHeight: "20px",
  color: "#111827",
  background: "#ffffff",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  boxSizing: "border-box",
};

function validate(values: AddressFormValues): AddressFormErrors {
  const errors: AddressFormErrors = {};

  if (!values.addressLine1.trim()) errors.addressLine1 = "" ;
  if (!values.city.trim()) errors.city = "This field is required.";
  if (!values.state.trim()) errors.state = "This field is required.";
  if (!values.zipCode.trim()) errors.zipCode = "This field is required.";
  if (!values.country.trim()) errors.country = "This field is required.";

  return errors;
}

export default function AddAddressModal({
  open,
  loading = false,
  title = "Add Your Address",
  subtitle = "Required to approve this proof",
  notice = "Please add your address to continue. This will be used for billing and shipping when preparing your order.",
  initialValues,
  onClose,
  onSubmit,
}: AddAddressModalProps) {
  const mergedInitialValues = useMemo(
    () => buildInitialValues(initialValues),
    [initialValues]
  );

  const [values, setValues] = useState<AddressFormValues>(mergedInitialValues);
  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [optionalFields, setOptionalFields] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  useEffect(() => {
    if (open) {
      setValues(mergedInitialValues);
      setErrors({});
      setOptionalFields({
        firstName: "",
        lastName: "",
        phone: "",
      });
    }
  }, [open, mergedInitialValues]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const hasGoogleCoordinates =
    Number(values.latitude || 0) !== 0 &&
    Number(values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    setErrors((prev) => ({
      ...prev,
      city: undefined,
      state: undefined,
      zipCode: undefined,
      country: undefined,
    }));
  };

  const handleChange = (field: keyof AddressFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddressLine1Change = (value: string) => {
    setValues((prev) => ({
      ...prev,
      addressLine1: value,
      ...(hasGoogleCoordinates || !value.trim()
        ? {
            city: "",
            cityCode: "",
            state: "",
            stateCode: "",
            zipCode: "",
            country: US_COUNTRY_DEFAULTS.country,
            countryCode: US_COUNTRY_DEFAULTS.countryCode,
            latitude: 0,
            longitude: 0,
          }
        : !prev.country.trim()
          ? US_COUNTRY_DEFAULTS
          : {}),
    }));

    setErrors((prev) => ({
      ...prev,
      addressLine1: undefined,
      ...(hasGoogleCoordinates || !value.trim()
        ? {
            city: undefined,
            state: undefined,
            zipCode: undefined,
            country: undefined,
          }
        : {}),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const submitValues: AddressFormValues = {
      ...values,
      country: values.country.trim() || US_COUNTRY_DEFAULTS.country,
      countryCode: String(values.countryCode || "").trim() || US_COUNTRY_DEFAULTS.countryCode,
    };

    const nextErrors = validate(submitValues);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    await onSubmit({
      firstName: optionalFields.firstName.trim(),
      lastName: optionalFields.lastName.trim(),
      phone: optionalFields.phone.trim(),
      label: String(submitValues.label || "").trim(),
      addressLine1: submitValues.addressLine1.trim(),
      addressLine2: submitValues.addressLine2.trim(),
      city: submitValues.city.trim(),
      state: submitValues.state.trim(),
      zipCode: submitValues.zipCode.trim(),
      country: submitValues.country.trim(),
      cityCode: String(submitValues.cityCode ?? "").trim(),
      stateCode: String(submitValues.stateCode ?? "").trim(),
      countryCode: String(submitValues.countryCode ?? "").trim(),
      latitude: Number(submitValues.latitude ?? 0),
      longitude: Number(submitValues.longitude ?? 0),
    });
  };

  const renderInput = (
    field: keyof AddressFormValues,
    label: string,
    placeholder: string,
    required = false,
    fullWidth = false,
    optional = false,
    disabled = false
  ) => {
    const hasError = Boolean(errors[field]);
    const disabledStyle: React.CSSProperties = disabled
      ? {
          background: "#f1f3f5",
          color: "#212529",
          border: "1px solid #cfd4da",
          cursor: "not-allowed",
        }
      : {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: fullWidth ? "1 / -1" : undefined }}>
        <label
          style={{
            fontSize: 15,
            lineHeight: "22px",
            fontWeight: 500,
            color: "#1f2937",
          }}
        >
          {label}
          {required ? <span style={{ color: "#ef4444" }}> *</span> : null}
          {optional ? <span style={{ color: "#94a3b8", fontWeight: 500 }}> (optional)</span> : null}
        </label>

        <input
          value={String(values[field] ?? "")}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          style={{
            ...inputBaseStyle,
            border: hasError ? "1px solid #f87171" : inputBaseStyle.border,
            boxShadow: hasError ? "0 0 0 2px rgba(239, 68, 68, 0.08)" : "none",
            ...disabledStyle,
          }}
          disabled={disabled}
          autoComplete="new-password"
          autoCorrect="off"
          spellCheck={false}
        />

        {hasError ? (
          <span style={{ fontSize: 14, lineHeight: "20px", color: "#ef4444" }}>{errors[field]}</span>
        ) : null}
      </div>
    );
  };

  const renderStateField = () => {
    const hasError = Boolean(errors.state);
    const labelStyle: React.CSSProperties = {
      fontSize: 15,
      lineHeight: "22px",
      fontWeight: 500,
      color: "#1f2937",
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <label style={labelStyle}>
          State<span style={{ color: "#ef4444" }}> *</span>
        </label>
        <input
          value={values.state}
          placeholder="GA"
          style={{
            ...inputBaseStyle,
            background: "#f1f3f5",
            color: "#212529",
            border: "1px solid #cfd4da",
            cursor: "not-allowed",
          }}
          disabled
          autoComplete="new-password"
          autoCorrect="off"
          spellCheck={false}
        />
        {hasError ? (
          <span style={{ fontSize: 14, lineHeight: "20px", color: "#ef4444" }}>{errors.state}</span>
        ) : null}
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      className="add-address-modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        fontFamily: "Roboto, sans-serif",
        background: "rgba(17, 24, 39, 0.45)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 10050,
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="add-address-modal-card"
        style={{
          width: "100%",
          maxWidth: 560,
          maxHeight: "92vh",
          background: "#ffffff",
          borderRadius: 22,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Back"
            className="public-address-back-btn"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: "transparent",
              color: "#64748b",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={18} color="#64748b" strokeWidth={2.2} />
          </button>

          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: "#edf4ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MapPin size={18} color="#2563eb" />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}  className="public-address-modal-root">
            <div style={{ fontSize: 16, lineHeight: "24px", fontWeight: 700, color: "#1f2937" }}>{title}</div>
            <div style={{ fontSize: 14, lineHeight: "20px", color: "#6b7280", marginTop: 1 }}>{subtitle}</div>
          </div>

          <button
            type="button"
            className="public-address-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} color="#6b7280" strokeWidth={2.2} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto", overflow: "hidden" }}
        >
          <div
            className="add-address-modal-scroll"
            style={{
              padding: "18px 22px 20px",
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "calc(92vh - 148px)",
              scrollbarWidth: "auto",
              scrollbarColor: "#94a3b8 #e8ecf1",
            }}
          >
            {notice ? (
              <div style={{ display: "none" }}>{notice}</div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }} className="public-address-modal-root">
              <div
                className="add-address-two-col"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 14,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 15, lineHeight: "22px", fontWeight: 500, color: "#1f2937" }}>
                    First Name<span style={{ color: "#94a3b8", fontWeight: 500 }}> (optional)</span>
                  </label>
                  <input
                    value={optionalFields.firstName}
                    onChange={(e) => setOptionalFields((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Sarah"
                    style={inputBaseStyle}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 15, lineHeight: "22px", fontWeight: 500, color: "#1f2937" }}>
                    Last Name<span style={{ color: "#94a3b8", fontWeight: 500 }}> (optional)</span>
                  </label>
                  <input
                    value={optionalFields.lastName}
                    onChange={(e) => setOptionalFields((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Johnson"
                    style={inputBaseStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 15, lineHeight: "22px", fontWeight: 500, color: "#1f2937" }}>
                  Phone Number<span style={{ color: "#94a3b8", fontWeight: 500 }}> (optional)</span>
                </label>
                <input
                  value={optionalFields.phone}
                  onChange={(e) => setOptionalFields((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (404) 555-0100"
                  style={inputBaseStyle}
                />
              </div>

              <div style={{ height: 1, background: "#efefef"}} />

              {renderInput("label", "Label", "e.g. Office, Warehouse, Home…", false, true, true)}

              <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "1 / -1" }}>
                <label
                  style={{
                    fontSize: 15,
                    lineHeight: "22px",
                    fontWeight: 500,
                    color: "#1f2937",
                  }}
                >
                  Address Line 1
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>
                <AddressAutocomplete
                  id="addressLine1"
                  name="addressLine1"
                  value={values.addressLine1}
                  onChange={handleAddressLine1Change}
                  onSelect={(picked) => {
                    setValues((prev) => ({
                      ...prev,
                      addressLine1: picked.addressLine1,
                      city: picked.city,
                      cityCode: picked.cityCode,
                      state: picked.state,
                      stateCode: picked.stateCode,
                      zipCode: picked.zipcode,
                      country: picked.country,
                      countryCode: picked.countryCode,
                      latitude: picked.latitude,
                      longitude: picked.longitude,
                    }));
                    setErrors((prev) => ({ ...prev, addressLine1: undefined }));
                    clearAddressDetailErrors();
                  }}
                  className="form-control br-8"
                  placeholder="123 Main Street"
                  autoComplete="new-password"
                  error={errors.addressLine1}
                />
                {errors.addressLine1 ? (
                  <span style={{ fontSize: 14, lineHeight: "20px", color: "#ef4444" }}>
                    {errors.addressLine1}
                  </span>
                ) : null}
              </div>
              {renderInput("addressLine2", "Address Line 2", "Apt, Suite, Unit (optional)", false, true, false)}

              <div
                className="add-address-two-col"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 16,
                }}
              >
                {renderInput("city", "City", "Atlanta", true, false, false, true)}
                {renderStateField()}
              </div>

              <div
                className="add-address-two-col"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 16,
                }}
              >
                {renderInput("zipCode", "Zip Code", "30301", true, false, false, true)}
                {renderInput("country", "Country", "United States", true, false, false, true)}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              padding: "16px 22px",
              borderTop: "1px solid #e5e7eb",
              background: "#fbfbfc",
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn back-btn surface-btn fw-bold fs-16"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary table-addbutton"
            >
              {/* <MapPin size={15} /> */}
              {loading ? "Adding..." : "Add Address"}
            </button>
          </div>
        </form>

        <style>{`
          .css-5o29mi-placeholder {
            color: #424851 !important;
          }
          .css-5o29mi-placeholder::placeholder {
            color: #424851 !important;
          }
          .rs__control {
            background-color: transparent !important;
          }
          .public-address-modal-root,
          .public-address-modal-root * {
            font-family: Roboto, sans-serif !important;
          }
          .public-address-modal-root input,
          .public-address-modal-root select,
          .public-address-modal-root textarea,
          .public-address-modal-root button {
            font-family: Roboto, sans-serif !important;
          }
          @media (max-width: 640px) {
            .add-address-two-col {
              grid-template-columns: 1fr !important;
            }
          }
          .public-address-modal-root input::placeholder,
          .public-address-modal-root textarea::placeholder {
            color: #9ca3af;
          }
          .public-address-modal-root .form-control {
            min-height: 40px;
            padding-top: 9px;
            padding-bottom: 9px;
          }
          .public-address-back-btn,
          .public-address-close-btn {
            appearance: none !important;
            -webkit-appearance: none !important;
            width: 36px !important;
            height: 36px !important;
            min-width: 36px !important;
            min-height: 36px !important;
            padding: 0 !important;
            margin: 0 !important;
            border-radius: 8px !important;
            background: transparent !important;
            box-shadow: none !important;
            cursor: pointer !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 0 !important;
            flex-shrink: 0 !important;
          }
          .public-address-back-btn {
            border: 1px solid #e5e7eb !important;
            border-radius: 10px !important;
          }
          .public-address-close-btn {
            border: 0 !important;
          }
          .public-address-back-btn:hover,
          .public-address-close-btn:hover {
            background: #f3f4f6 !important;
            box-shadow: none !important;
          }
          .public-address-back-btn svg,
          .public-address-close-btn svg {
            display: block !important;
            width: 18px !important;
            height: 18px !important;
          }
          .add-address-modal-scroll {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-gutter: stable;
            scrollbar-width: auto;
            scrollbar-color: #94a3b8 #e8ecf1;
          }
          .add-address-modal-scroll::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          .add-address-modal-scroll::-webkit-scrollbar-track {
            background: #e8ecf1;
            border-radius: 8px;
          }
          .add-address-modal-scroll::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 8px;
          }
          .add-address-modal-scroll::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
          @media (max-width: 575.98px) {
            .add-address-modal-overlay {
              align-items: flex-start;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
              padding-top: max(12px, env(safe-area-inset-top, 0px));
              padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
            }
            .add-address-modal-card {
              max-height: calc(100vh - 24px) !important;
            }
            .add-address-modal-scroll {
              max-height: calc(100vh - 172px) !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export { BillingShippingApprovalModal } from "./BillingShippingApprovalModal";
export type { BillingShippingApprovalModalProps } from "./BillingShippingApprovalModal";

/*
Usage:

const [open, setOpen] = useState(false);

<AddAddressModal
  open={open}
  onClose={() => setOpen(false)}
  onSubmit={async (values) => {
    console.log(values);
    setOpen(false);
  }}
/>
*/
